import { Component, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material';
import { UserService } from '../../service/user.service';
import { ConfigService } from '../../service/config.service';
import { BrowserUtil } from '../../utils/browser.util';
import { TermsAndConditionsCibilPopupComponent } from './consent-popups/termsAndConditions/termsAndConditions.component';
import { OtpVerifyPopupComponent, otpVerifyPopupData } from './otp-verify-popup/otp-verify-popup.component';
import { LoginService } from '../../service/login.service';
import { LoginHelper } from '../../helpers/login.helper';
import { Keys, StorageService } from '../../service/storage.service';
import { LoginInteractor } from '../../interactors/login.interactor';
import { first } from 'lodash';
import { panExists } from '../../model/panExists.model';
import { Toaster, Type } from '../../toaster/Toaster';
import { CreateUserResponse, User } from '../../model/user.model';
import { CibilDefaultValues, CibilRedirectionData, CibilRequest, CibilResponse, ResponseStatus, ResponseStatusCode, UserInputTypes } from '../../model/cibil/cibil-service.model';
import { CibilService } from '../../service/cibil/cibil.service';
import { Logger } from '../../utils/Logger';
import { LoanApplicationService } from '../../service/loan-application.service';
import { LivelihoodInformationService } from '../../service/livelihood-information.service';
import { LivelihoodInformation } from '../../model/livelihood-information.model';
import { LoanDetailsService } from '../../service/loan-details.service';
import { AlternativeMobilePopupComponent } from './alternative-mobile-popup/alternative-mobile-popup.component';
import { UserAddress } from '../../model/user-address.model';
import { UserAddressService } from '../../service/user-address.service';
import moment = require('moment');
import { ConsentType } from '../../model/ConsentAudit';
import { ConsentService } from '../../service/consent.service';
import { EmailVerifyPopupComponent } from './email-verify-popup/email-verify-popup.component';
import { HeaderComponent } from '../../header/header.component';

@Component({
    selector: 'app-user-details',
    templateUrl: './user-details.component.html',
    styleUrls: ['./user-details.component.scss', '../../../../src/app/views/common/button/primary-button.component.scss']
})

export class UserDetailsComponent {
    @ViewChild(HeaderComponent, { static: true })
    private headerComponent: HeaderComponent;
    public alternativeMobileNumberEntered: string;
    public alternativeMobileOtpFailureCount: number = 0;
    public buttonText: string = 'Next';
    public cibilService: CibilService;
    public consentService: ConsentService;
    public currentStep: number = 1;
    public emailOtpFailureCount: number = 0;
    public employeeTypes: { salaried: string, selfEmployed: string, professional: string } = {
        salaried: 'SALARIED',
        selfEmployed: 'SELF_EMPLOYED',
        professional: 'PROFESSIONAL'
    };
    public isEmailDisabled: boolean = true;
    public isLoading: boolean = false;
    public isMobileVerified: boolean = false;
    public isPanExists: boolean = false;
    public isReadOnly: boolean = true;
    public karzaNameResponse: { name: string };
    public livilehoodInformationService: LivelihoodInformationService;
    public loanApplicationService: LoanApplicationService;
    public loanDetailsService: LoanDetailsService;
    public loginService: LoginService;
    private readonly matDialog: MatDialog;
    public maskedOriginalPanName: string;
    private minimumIncome: number = 0;
    public MinimumAnnualSalary: number;
    public MinimumSalary: number;
    public MaximumSalary: number;
    public originalPanName: string = null;
    public panExistsMessage: string = 'The PAN you entered is registered with us';
    public panExistsResponse: panExists;
    public previousCibilResponse: CibilResponse = null;
    public shouldDisableButton: boolean = false;
    public shouldDisableConsentCheckBoxes: boolean = false;
    public shouldDisableDobField: boolean = false;
    public shouldDisableEmailField: boolean = false;
    public shouldShowMobileField: boolean = false;
    public shouldShowNameField: boolean = false;
    public tellUsAboutYourselfForm: FormGroup;
    public userAddressService: UserAddressService;
    public userDetails: User = null;
    public userService: UserService;
    private user: User;
    public livelihoodDetails: LivelihoodInformation = null;
    public userCreationToken: string;
    public emailOtpResendEnabled: boolean = true;
    public emailOtpResendAttemptUsed: boolean = false;
    public alternativeMobileOtpResendEnabled: boolean = true;
    public alternativeMobileOtpResendAttemptUsed: boolean = false;
    public isNoHistory: boolean = false;

    constructor(
        matDialog: MatDialog,
        userService: UserService,
        userAddressService: UserAddressService,
        loginService: LoginService,
        cibilService: CibilService,
        consentService: ConsentService,
        loanApplicationService: LoanApplicationService,
        livilehoodInformationService: LivelihoodInformationService,
        loanDetailsService: LoanDetailsService,
        public formBuilder: FormBuilder,
        public configService: ConfigService,
        private loginHelper: LoginHelper,
        private storageService: StorageService,
        private loginInteractor: LoginInteractor,
        private browserUtil: BrowserUtil
    ) {
        this.matDialog = matDialog;
        this.MinimumSalary = configService.getMinimumIncome();
        this.MaximumSalary = configService.getMaximumIncome();
        this.userService = userService;
        this.userAddressService = userAddressService;
        this.loginService = loginService;
        this.cibilService = cibilService;
        this.consentService = consentService;
        this.loanApplicationService = loanApplicationService;
        this.livilehoodInformationService = livilehoodInformationService;
        this.loanDetailsService = loanDetailsService;
    }

    async ngOnInit(): Promise<void> {
        this.initTellUsAboutYourselfForm(this.formBuilder);
    }

    // [a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,3} regex used for email in cibil

    // Form Intitialisation ********************Start********************
    private initTellUsAboutYourselfForm(formBuilder: FormBuilder): void {
        this.tellUsAboutYourselfForm = formBuilder.group({
            panNumber: new FormControl(null, [Validators.required, Validators.minLength(10), Validators.maxLength(10), Validators.pattern(/^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/)]),
            mobileNumber: new FormControl(null, [Validators.required, Validators.minLength(10), Validators.maxLength(10), Validators.pattern(/^[1-9]{1}[0-9]{9}$/)]),
            name: new FormControl({ value: null, disabled: true }, [Validators.required, Validators.pattern(/^(([a-zA-Z0-9()|@&!#$%.,{}*/\\[\]])+ ?([a-zA-Z0-9()|@&!#$%*.,{}/\\[\]])*){1,35}$/)]),
            email: new FormControl(null, [Validators.required, Validators.email, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]),
            employeeType: new FormControl(null, Validators.required),
            monthlyIncome: new FormControl(null, [Validators.required, Validators.max(this.MaximumSalary), Validators.min(this.MinimumSalary), Validators.pattern(/^[1-9]/)]),
            termsAndConditionConsent: new FormControl(false, Validators.requiredTrue),
            promotionsConsent: new FormControl(false, Validators.requiredTrue),
            dob: new FormControl(null, Validators.required),
            pincode: new FormControl(null, [Validators.required, Validators.minLength(6), Validators.maxLength(6)])
        });
    }
    // Form Intitialisation ********************End********************


    // Form Validation Errors ********************Start********************
    private getControlName(c: AbstractControl): string | null {
        const formGroup = c.parent.controls;
        return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
    }

    public getErrorMessage(control: AbstractControl): string {
        const controlName = this.getControlName(control);
        const errors = this.allErrorMessages();
        const message = '';
        if (control.errors) {
            const occurredError = first(Object.keys(control.errors));
            const predefinedErrorsForControl = errors[controlName];
            const result = predefinedErrorsForControl.find(predefinedError => {
                return predefinedError.type === occurredError;
            });
            if (result) {
                return result.message;
            } else {
                console.log(`Could not find error message for error: ${occurredError}`);
            }
        }
        return message;
    }

    public allErrorMessages() {
        return {
            panNumber: [
                {
                    type: 'required',
                    message: 'Please enter a pan number'
                },
                {
                    type: 'minlength',
                    message: `Please provide a valid PAN`
                },
                {
                    type: 'maxlength',
                    message: `Please provide a valid PAN`
                },
                {
                    type: 'pattern',
                    message: 'Please provide a valid PAN'
                }
            ],
            mobileNumber: [
                {
                    type: 'required',
                    message: 'Please enter a mobile number'
                },
                {
                    type: 'minlength',
                    message: `Please provide a valid mobile number`
                },
                {
                    type: 'maxlength',
                    message: `Please provide a valid mobile number`
                },
                {
                    type: 'pattern',
                    message: 'Please provide a valid mobile number'
                }
            ],
            name: [
                {
                    type: 'required',
                    message: 'Please enter a Name'
                },
                {
                    type: 'pattern',
                    message: 'Please provide a valid name'
                }
            ],
            email: [
                {
                    type: 'required',
                    message: 'Please enter a email'
                },
                {
                    type: 'email',
                    message: 'Please enter a valid email'
                },
                {
                    type: 'pattern',
                    message: 'Please enter a valid email'
                },
            ],
            employeeType: [
                {
                    type: 'required',
                    message: 'Please select a employement type'
                }
            ],
            monthlyIncome: [
                {
                    type: 'required',
                    message: 'Please enter your income'
                },
                {
                    type: 'minIncomeError',
                    message: `Income should be greater than Rs ${this.minimumIncome}`
                },
                {
                    type: 'max',
                    message: `Income should be lesser than Rs ${this.MaximumSalary}`
                },
                {
                    type: 'pattern',
                    message: `Please enter a valid amount`
                },
            ],
            dob: [
                {
                    type: 'required',
                    message: 'Please enter your date of birth'
                }
            ],
            pincode: [
                {
                    type: 'required',
                    message: 'Please enter your pincode'
                },
                {
                    type: 'minlength',
                    message: 'Please enter a valid pincode'
                },
                {
                    type: 'maxlength',
                    message: 'Please enter a valid pincode'
                }
            ]
        };
    }
    // Form Validation Errors ********************End********************


    // Cibil Popup ********************Start********************
    public termsAndConditionClick() {
        this.openTermsAndConditionsPopup();
    }

    public openTermsAndConditionsPopup() {
        const matDialogConfig: MatDialogConfig = {
            panelClass: 'terms-and-conditions-cibil'
        };
        this.matDialog.open(TermsAndConditionsCibilPopupComponent, matDialogConfig)
    }

    // this is to handle finzy terms and condition click
    public openFromMobile(e, download = false) {
        let webkit = this.browserUtil.nativeWindow.webkit;
        if (webkit) {
            e.preventDefault();
            e.stopPropagation();

            let url = e.target.href;
            if (!url) {
                url = e.target.parentNode.href;
            }
            var msg = JSON.stringify({ 'action': (download) ? 'download_file' : 'open_external', 'url': url });
            webkit.messageHandlers.cordova_iab.postMessage(msg);
        }
    }
    // Cibil Popup ********************End********************


    // Steps Navigation *********************Start******************
    public canNavigate(): boolean {
        switch (this.currentStep) {
            case 1:
                if (!this.tellUsAboutYourselfForm.controls.panNumber.errors &&
                    !this.tellUsAboutYourselfForm.controls.termsAndConditionConsent.errors &&
                    !this.tellUsAboutYourselfForm.controls.promotionsConsent.errors) {
                    return true;
                }
                break;
            case 2:
                if (!this.tellUsAboutYourselfForm.controls.mobileNumber.errors) {
                    return true;
                }
                break;
            case 3:
                if (!this.tellUsAboutYourselfForm.controls.email.errors &&
                    !this.tellUsAboutYourselfForm.controls.employeeType.errors &&
                    !this.tellUsAboutYourselfForm.controls.monthlyIncome.errors && this.isMobileVerified) {
                    return true;
                }
                break;
            case 4:
                if (!this.tellUsAboutYourselfForm.controls.dob.errors &&
                    !this.tellUsAboutYourselfForm.controls.pincode.errors) {
                    return true;
                }
        }
    }

    public async prevNextStep() {
        try {
            this.isLoading = true;
            if (this.currentStep < 3) {
                switch (this.currentStep) {
                    case 1:
                        this.clearPreviousCibilResponse();
                        await this.handlePanCheckStage();
                        // this.openAlternativeMobilePopup();
                        break;
                    case 2:
                        this.clearPreviousCibilResponse();
                        await this.handleRegisterUserStage();
                        break;
                }
            } else {
                this.clearPreviousCibilResponse();
                await this.handleCibilApiCall();
            }
        } catch (error) {
            Logger.log(`Error occured ${error}`);
            Toaster.toast(`Something went wrong`, Type.Error);
        } finally {
            this.isLoading = false;
        }
    }

    private clearPreviousCibilResponse() {
        // this.emailOtpResendEnabled = true;
        this.emailOtpFailureCount = 0;
        // this.alternativeMobileOtpResendEnabled = true;
        this.alternativeMobileOtpFailureCount = 0;
        this.previousCibilResponse = null;
    }

    // Steps Navigation *********************End******************

    public handlePanChange() {
        if (this.currentStep == 2) {
            this.currentStep = 1;
            this.isPanExists = false;
        }
    }

    // to handle if pan is registered, then verify registered mobile number
    private async handlePanCheckStage() {
        const panNumber = this.tellUsAboutYourselfForm.value.panNumber.toUpperCase();
        try {
            this.panExistsResponse = await this.userService.checkPanExists(panNumber);
        } catch (err) {
            Toaster.toast(`An error occurred while checking pan details`, Type.Error);
            return;
        }

        // if pan is already registered verify registered mobile number
        if (this.panExistsResponse?.isExists) {
            this.isPanExists = true;
            let loginDetails: { id: string; maskedPhoneNumber: string; };
            try {
                loginDetails = await this.loginService.createLoginRequestWithPan(panNumber);
                if (!loginDetails.id) {
                    Toaster.toast(`An error occurred while sending OTP`, Type.Error);
                    return;
                }
            } catch (err) {
                Toaster.toast(`An error occurred while sending OTP`, Type.Error);
                return;
            }
            const data: otpVerifyPopupData = {
                mainHeading: 'Verify Your Mobile Number',
                subHeading: `Please enter the OTP sent to your mobile number`,
                infoMessage: 'The PAN you entered is registered with us',
                infoMessageType: 'normal',
                submitButtonText: 'Verify',
                BackButtonText: 'Back',
                popupType: 'mobileOtpVerify',
                mobileNumber: loginDetails.maskedPhoneNumber
            }
            this.openMobileNumberVerifyPopup(data, loginDetails.id);
        } else if (!this.panExistsResponse?.isExists) {
            this.shouldDisableConsentCheckBoxes = true;
            try {
                this.karzaNameResponse = await this.userService.getKarzaPanName(panNumber);
            } catch (err) {
                Toaster.toast(`An error occurred while getting name associated with pan`, Type.Error);
                return;
            }

            if (this.karzaNameResponse && this.karzaNameResponse?.name) {
                this.setName(this.karzaNameResponse?.name);
                // if pan is not registered and PAN name fetched successfully from KARZA take to next step
                this.currentStep++;
            }
        }
    }

    private setName(name: string) {
        this.originalPanName = name;
        if (this.originalPanName && this.originalPanName.length > 4) {
            this.maskedOriginalPanName = this.originalPanName.substring(0, 2) + this.originalPanName.substring(2, this.originalPanName.length - 2).replace(/[^ ]/g, '*') + this.originalPanName.substring(this.originalPanName.length - 2);
        } else {
            this.maskedOriginalPanName = this.originalPanName.substring(0, 1) + this.originalPanName.substring(1, this.originalPanName.length - 1).replace(/[^ ]/g, '*') + this.originalPanName.substring(this.originalPanName.length - 1);
        }
        this.tellUsAboutYourselfForm.controls['name'].setValue(this.maskedOriginalPanName);
    }

    // to handle if pan is not registered, then verify entered mobile number
    private async handleRegisterUserStage() {
        const panNumber = this.tellUsAboutYourselfForm.value.panNumber.toUpperCase();
        const mobileNumber = this.tellUsAboutYourselfForm.value.mobileNumber;
        let phoneVerificationRequestToken: string, phoneNumberVerificationToken: string;
        try {
            phoneVerificationRequestToken = await this.userService.verifyPan(panNumber, this.karzaNameResponse.name);
            phoneNumberVerificationToken = await this.userService.requestForPhoneVerification(mobileNumber, phoneVerificationRequestToken);
        } catch (err) {
            Toaster.toast(`An error occurred while sending OTP ${err}`, Type.Error);
            return;
        }
        const data: otpVerifyPopupData = {
            mainHeading: 'Verify Your Mobile Number',
            subHeading: `Please enter the OTP sent to your mobile number`,
            submitButtonText: 'Verify',
            BackButtonText: 'Back',
            popupType: 'mobileOtpVerify',
            mobileNumber: mobileNumber
        }
        this.openMobileNumberVerifyPopup(data, phoneNumberVerificationToken, phoneVerificationRequestToken);
    }

    // This is to handle popup to verify mobile number both registered and entered
    public openMobileNumberVerifyPopup(popupData: otpVerifyPopupData, token?: string, phoneNumberRequestToken?: string): void {
        let loginId = token;
        const matDialogConfig: MatDialogConfig = {
            height: 'auto',
            panelClass: 'mobile-number-verify-cibil',
            disableClose: true,
            data: popupData
        };
        const dialogRef = this.matDialog.open(OtpVerifyPopupComponent, matDialogConfig);

        dialogRef.afterOpened().subscribe(() => {
            dialogRef.componentInstance?.resendClicked.subscribe(async () => {
                if (this.isPanExists) {
                    try {
                        const loginDetails = await this.loginService.createLoginRequestWithPan(this.tellUsAboutYourselfForm.value.panNumber);
                        loginId = loginDetails.id;
                    } catch (err) {
                        Toaster.toast(`An error occurred while resending OTP ${err}`, Type.Error);
                        return;
                    }
                } else {
                    try {
                        await this.userService.requestForPhoneVerification(popupData.mobileNumber, phoneNumberRequestToken);
                    } catch (err) {
                        Toaster.toast(`An error occurred while resending OTP ${err}`, Type.Error);
                        return;
                    }
                }
            });
        })

        dialogRef.afterClosed().subscribe(async (otp) => {
            if (otp) {
                if (this.isPanExists) {
                    await this.verifyLoginPassword(token, otp);
                } else {
                    await this.registerUser(otp, token);
                }
            }
        })
    }

    private async verifyLoginPassword(loginId: string, otp: string) {
        try {
            this.user = await this.loginInteractor.verifyPassword(loginId, otp);
            this.headerComponent.onUserLoggedIn();
        } catch (err) {
            Toaster.toast(`An error occurred while verifying OTP ${err}`, Type.Error);
            return;
        }

        if (this.user.name) {
            this.setName(this.user.name);
        }

        if (this.user.email) {
            this.tellUsAboutYourselfForm.controls.email.setValue(this.user.email);
            this.shouldDisableEmailField = true;
        } else {
            this.currentStep = 3;
        }
        
        if (this.user.dob) {
            this.tellUsAboutYourselfForm.controls.dob.setValue(new Date(this.user.dob));
            this.shouldDisableDobField = true;
        }
        

        const pincode = await this.getLatestPostalCode(this.user);
        if (pincode) {
            this.tellUsAboutYourselfForm.controls.pincode.setValue(pincode);
        }

        this.isMobileVerified = true;
        this.shouldDisableConsentCheckBoxes = true;
        this.tellUsAboutYourselfForm.controls.mobileNumber.setValue(this.user.mobile_no);

        try {
            const consentsToSave: ConsentType[] = [];
            const [creditCheckConsent, marketingConsent] = await Promise.all([
                await this.consentService.getConsentsForUserByType(this.user._id, ConsentType.CREDIT_HISTORY_CHECK),
                await this.consentService.getConsentsForUserByType(this.user._id, ConsentType.MARKETING_CONSENT)
            ]);
            if (!creditCheckConsent.length) consentsToSave.push(ConsentType.CREDIT_HISTORY_CHECK);
            if (!marketingConsent.length) consentsToSave.push(ConsentType.MARKETING_CONSENT);
            if (consentsToSave && consentsToSave.length) await this.saveConsentForUser(this.user._id, consentsToSave);
        } catch (err) {
            Logger.error(`An error occured when saving consents for user ${this.user._id}`);
            Toaster.toast('Something went wrong on saving consents.', Type.Error);
        }

        try {
            this.livelihoodDetails = await this.getLatestLivelihoodInformation(this.user._id);
            if (!this.livelihoodDetails) {
                this.currentStep = 3;
            } else {
                this.handleCibilApiCall();
            }
        } catch (err) {
            Toaster.toast(`An error occurred ${err}`, Type.Error);
            return;
        }
    }

    private async getLatestLivelihoodInformation(userId: string) {
        const loanApplications = await this.loanApplicationService.getLoanApplicationsByUserId(userId);
        let livelihoodDetails: LivelihoodInformation;
        for (let i = loanApplications.length - 1; i >= 0; i--) {
            const res = await this.livilehoodInformationService.getByLoanApplicationId(loanApplications[i].id);
            if (res && res.income) {
                livelihoodDetails = res;
                break;
            }
        }
        return livelihoodDetails;
    }

    private async getLatestPostalCode(user: User) {
        let addresses: UserAddress[] = [];
        if (user.correspondence_address) {
            addresses.push(user.correspondence_address);
        }
        if (user.addresses && user.addresses.length) {
            for (let i = 0; i < user.addresses.length; i++) {
                const res = await this.userAddressService.get(user.addresses[i]);
                addresses.push(res);
            }
        }
        if (addresses && addresses.length) {
            addresses.sort((a, b) => {
                return moment(b.updatedAt).toDate().getTime() - moment(a.updatedAt).toDate().getTime();
            })
            return addresses[0].pin_code;
        }
        return null;
    }

    private async registerUser(otp: any, token: string) {
        let userCreationToken: string, user: CreateUserResponse;
        try {
            userCreationToken = await this.userService.verifyPhoneForRegistration(otp, token);
        } catch (err) {
            Toaster.toast(`An error occurred while verifying OTP ${err}`, Type.Error);
            return;
        }
        this.isMobileVerified = true;
        this.currentStep++;
        try {
            user = await this.userService.createUser(userCreationToken);
        } catch (err) {
            Toaster.toast(`Something went wrong`, Type.Error);
            return;
        }

        this.storageService.saveItem(Keys.IS_OTP_VERIFIED, true.toString());
        this.storageService.saveItem(Keys.PAN_VERIFIED, true.toString());
        this.storageService.saveItem(Keys.PROFILE_ID, user.userId);
        this.storageService.saveItem(Keys.TOKEN, user.authToken);
        this.storageService.saveItem(Keys.NAME, user.name);
        this.headerComponent.onUserLoggedIn();
        try {
            await this.saveConsentForUser(user.userId);
        } catch (err) {
            Logger.error(`An error occured when saving consents for user ${user.userId}`);
            Toaster.toast('Something went wrong on saving consents.', Type.Error);
            return;
        }

        try {
            this.user = await this.userService.getProfileDetails(user.userId);
        } catch (err) {
            Toaster.toast(`Something went wrong`, Type.Error);
            return;
        }
    }

    private async saveConsentForUser(userId: string, consentsToStore?: ConsentType[]) {
        const consents = (consentsToStore && consentsToStore.length ? consentsToStore : null) || [ConsentType.CREDIT_HISTORY_CHECK, ConsentType.MARKETING_CONSENT];
        for (let i = 0; i < consents.length; i++) {
            try {
                await this.consentService.saveConsent(consents[i]);
            }
            catch (e) {
                Logger.log(`Failed to save the consent of type ${consents[i]} for user ${userId}`)
            }
        }
    }

    public async openEmailOtpVerifyPopup(email: string, statusMessage: string): Promise<void> {
        const matDialogConfig: MatDialogConfig = {
            height: 'auto',
            panelClass: 'email-verify-popup',
            disableClose: true,
            data: {
                email: this.getMaskedEmail(email),
                mobileNumber: this.userDetails.mobile_no,
                wrongOtpEntered: this.emailOtpFailureCount ? this.emailOtpFailureCount : null,
                errorMessage: statusMessage,
                resendEnabled: this.emailOtpResendEnabled
            }
        };
        if (this.emailOtpFailureCount < 3) {
            const dialogRef = this.matDialog.open(EmailVerifyPopupComponent, matDialogConfig);

            dialogRef.afterOpened().subscribe(async () => {
                dialogRef.componentInstance?.resendClicked.subscribe(async () => {
                    this.emailOtpResendEnabled = false;
                    this.emailOtpResendAttemptUsed = true;
                    await this.handleCibilApiCall();
                    if (dialogRef.componentInstance) {
                        dialogRef.componentInstance.resendEnabled = false;
                    }
                });
            })

            const otp = await dialogRef.afterClosed().toPromise();
            if (otp) {
                try {
                    this.emailOtpFailureCount++;
                    await this.handleCibilApiCall(otp);
                } catch (err) {
                    this.emailOtpFailureCount = 0;
                    this.previousCibilResponse = null;
                    Toaster.toast(`Something went wrong`, Type.Error);
                }
            } else {
                this.previousCibilResponse = null;
                this.emailOtpFailureCount = 0;
                const cibilData: CibilRedirectionData = {
                    creditScore: null,
                    creditReportId: null,
                    pan: this.userDetails.pan.pan_no,
                    livelihoodInfo: null,
                    personalInfo: null,
                    noHistory: null
                }
                this.redirectToCibilDashboard(cibilData);
            }
        } else {
            this.previousCibilResponse = null;
            this.handleCibilApiCall(null, 'dummycustomer@finzy.com')
        }

    }

    public async openAlternativeMobilePopup(data?): Promise<void> {
        const matDialogConfig: MatDialogConfig = {
            height: 'auto',
            panelClass: 'alternative-mobile-popup',
            disableClose: true,
            data: {
                mobile: this.tellUsAboutYourselfForm.value.mobileNumber || this.userDetails.mobile_no,
                ...data
            }
        };

        const cibilData: CibilRedirectionData = {
            creditScore: null,
            creditReportId: null,
            pan: this.userDetails.pan.pan_no,
            livelihoodInfo: null,
            personalInfo: null,
            noHistory: null
        }

        const dialogRef = this.matDialog.open(AlternativeMobilePopupComponent, matDialogConfig);

        dialogRef.afterOpened().subscribe(async () => {
            dialogRef.componentInstance?.alternativeMobileNumber.subscribe(async () => {
                let alternativeNumber = dialogRef.componentInstance?.alternativeMobileForm.value.mobileNumber;
                this.alternativeMobileNumberEntered = alternativeNumber;
                if (this.alternativeMobileOtpFailureCount < 3) {
                    await this.handleCibilApiCall(alternativeNumber);
                    if (dialogRef.componentInstance) {
                        dialogRef.componentInstance.buttonText = 'VERIFY';
                        dialogRef.componentInstance.isLoading = false;
                        dialogRef.componentInstance.resendEnabled = this.alternativeMobileOtpResendEnabled;
                        dialogRef.componentInstance.disableMobileNumberField = true;
                    }
                } else {
                    Toaster.toast(`You have exceeded the limit for OTP verification.`, Type.Error);
                    this.previousCibilResponse = null;
                    this.redirectToCibilDashboard(cibilData);
                }
            });

            dialogRef.componentInstance?.resendCicked.subscribe(async () => {
                this.alternativeMobileOtpResendEnabled = false;
                this.alternativeMobileOtpResendAttemptUsed = true;
                await this.handleCibilApiCall(this.alternativeMobileNumberEntered);
                if (dialogRef.componentInstance) {
                    dialogRef.componentInstance.isLoading = false;
                    dialogRef.componentInstance.resendEnabled = false;
                }
            });
        });

        const otp = await dialogRef.afterClosed().toPromise();
        if (otp) {
            this.handleCibilApiCall(otp);
        } else {
            this.previousCibilResponse = null;
            this.emailOtpFailureCount = 0;
            this.redirectToCibilDashboard(cibilData);
        }
    }

    private getMaskedEmail(email: string): string {
        return email.substring(0, 2) + email.substring(2, email.indexOf('@')).replace(/[^ ]/g, '*') + email.substring(email.indexOf('@') - 2);
    }

    private async storeAlternativeMobileNumber(): Promise<void> {
        try {
            if (this.alternativeMobileNumberEntered) {
                const contactInfo = {
                    userId: this.user._id,
                    isVerified: true,
                    isPrimary: false,
                    mobileNumber: this.alternativeMobileNumberEntered.toString()
                };
                const contactInfoCreated = await this.userService.createContactInfo(contactInfo);
                if (contactInfoCreated) {
                    await this.userService.createOrUpdateCibilContacts(this.user._id, contactInfoCreated.id);
                }
            }
        } catch (err) {
            Logger.error(`Error occured while storing alternative mobile number ${err}`);
        }
    }

    private async handleCibilApiCall(authAnswer?: string, email?: string) {
        const userId = this.storageService.getItem(Keys.PROFILE_ID);

        try {
            this.isLoading = true;
            this.userDetails = await this.userService.getProfileDetails(userId);
            if(this.userDetails && !this.userDetails.email && this.currentStep >= 3) {
                await this.userService.saveEmail(this.tellUsAboutYourselfForm.value.email, userId);
            }
        } catch (err) {
            this.isLoading = false;
            Logger.log(`Error occured ${this.userDetails._id} ${err}`);
            Toaster.toast(`Something went wrong`, Type.Error);
            return;
        }

        const res = await this.callCibilApi(this.userDetails, authAnswer, email);

        if (res && res.responseStatus && res.responseStatus.status) {
            switch (res.responseStatus.status) {
                case ResponseStatus.SUCCESS: {
                    let personalInfo = { email: this.userDetails.email || this.tellUsAboutYourselfForm.value.email };
                    let livelihoodInfo = {
                        employmentType: (this.livelihoodDetails && this.livelihoodDetails.employeeType) || this.tellUsAboutYourselfForm.value.employeeType,
                        income: (this.livelihoodDetails && this.livelihoodDetails.income) || this.tellUsAboutYourselfForm.value.monthlyIncome
                    };
                    const data: CibilRedirectionData = {
                        creditScore: res.score,
                        creditReportId: res.id,
                        pan: this.userDetails.pan.pan_no,
                        personalInfo,
                        livelihoodInfo,
                        noHistory: this.isNoHistory
                    }
                    await this.storeAlternativeMobileNumber();
                    await this.redirectToCibilDashboard(data);
                    break;
                }
                case ResponseStatus.FAILURE: {
                    if (res.responseStatus.statusCode === ResponseStatusCode.USER_NOT_FOUND) {
                        if (this.currentStep === 1) {
                            // if user was in step 1 then check for livelihood to decide which step we need to move next
                            this.livelihoodDetails = await this.getLatestLivelihoodInformation(this.userDetails._id);
                            if (!this.livelihoodDetails) {
                                this.currentStep = 3;
                            } else {
                                this.tellUsAboutYourselfForm.controls.employeeType.setValue(this.livelihoodDetails.employeeType);
                                this.tellUsAboutYourselfForm.controls.monthlyIncome.setValue(this.livelihoodDetails.income);
                                this.currentStep = 4;
                            }
                        } else if (this.currentStep === 3) {
                            this.currentStep++;
                        } else {
                            Toaster.toast(`No details found for the information provided in CIBIL`, Type.Error);
                            this.isNoHistory = true;
                            const data: CibilRedirectionData = {
                                creditScore: null,
                                creditReportId: null,
                                pan: this.userDetails.pan.pan_no,
                                personalInfo: null,
                                livelihoodInfo: null,
                                noHistory: this.isNoHistory
                            }
                            this.redirectToCibilDashboard(data);
                        }
                    } else if (res.responseStatus.statusCode === ResponseStatusCode.INVALID_INPUT &&
                        this.previousCibilResponse.responseStatus.statusCode === ResponseStatusCode.EMAIL_VERIFICATION_CODE_REQUIRED) {
                        this.openEmailOtpVerifyPopup(this.userDetails.email || this.tellUsAboutYourselfForm.value.email, res.responseStatus.statusMessage);
                    } else if (res.responseStatus.statusCode === ResponseStatusCode.INVALID_INPUT &&
                        this.previousCibilResponse.responseStatus.statusCode === ResponseStatusCode.MOBILE_VERIFICATION_CODE_REQUIRED) {
                        this.alternativeMobileOtpFailureCount++;
                        let data = {
                            alternativeMobileNumberEntered: this.alternativeMobileNumberEntered,
                            wrongOtpEntered: this.alternativeMobileOtpFailureCount,
                            buttonText: 'VERIFY',
                            errorMessage: res.responseStatus.statusMessage,
                            resendEnabled: this.alternativeMobileOtpResendEnabled,
                            disableMobileNumberField: true
                        }
                        this.openAlternativeMobilePopup(data);
                    } else if(res.responseStatus.statusCode === ResponseStatusCode.REGENERATE_VERIFICATION_CODE_NOT_ALLOWED) {
                        Toaster.toast(`Regeneration of OTP is not allowed at the moment.`, Type.Error);
                    } else {
                        Logger.log(`FAILURE for ${this.userDetails.name} ${res.responseStatus.statusMessage}`);
                        Toaster.toast(`User authentication failed for the details provided. Please try again after sometime.`, Type.Error);
                        const data: CibilRedirectionData = {
                            creditScore: null,
                            creditReportId: null,
                            pan: this.userDetails.pan.pan_no,
                            personalInfo: null,
                            livelihoodInfo: null
                        }
                        this.redirectToCibilDashboard(data);
                    }
                    break;
                }
                case ResponseStatus.IN_PROGRESS: {
                    this.previousCibilResponse = res;
                    if (res.responseStatus.statusCode === ResponseStatusCode.EMAIL_VERIFICATION_CODE_REQUIRED) {
                        if (this.emailOtpResendEnabled) {
                            let email = this.tellUsAboutYourselfForm.value.email;
                            this.openEmailOtpVerifyPopup(email, res.responseStatus.statusMessage);
                        }
                    } else if (res.responseStatus.statusCode === ResponseStatusCode.ALTERNATE_MOBILE_NO_REQUIRED) {
                        if (this.alternativeMobileOtpResendEnabled) {
                            const data = {
                                errorMessage: res.responseStatus.statusMessage
                            };
                            this.openAlternativeMobilePopup(data);
                        }
                    } else if (res.responseStatus.statusCode === ResponseStatusCode.MOBILE_VERIFICATION_CODE_REQUIRED) {
                    } else {
                        const data: CibilRedirectionData = {
                            creditScore: null,
                            creditReportId: null,
                            pan: this.userDetails.pan.pan_no,
                            personalInfo: null,
                            livelihoodInfo: null,
                            noHistory: null
                        }
                        this.redirectToCibilDashboard(data);
                    }
                    break;
                }
            }
        }
    }

    private async callCibilApi(user: any, authAnswer?: string, email?: string): Promise<CibilResponse> {
        let cibilRequestData: CibilRequest = {
            userId: user._id,
            generatedUserId: user.generated_user_id,
            name: user.name,
            gender: CibilDefaultValues.GENDER,
            address: CibilDefaultValues.ADDRESS,
            city: CibilDefaultValues.CITY,
            state: CibilDefaultValues.STATE,
            postal: CibilDefaultValues.POSTAL,
            phoneNumber: user.mobile_no,
            emailId: email || user.email || this.tellUsAboutYourselfForm.value.email,
            panNumber: user.pan.pan_no,
            owner: CibilDefaultValues.OWNER
        };
        if (this.currentStep === 1) {
            cibilRequestData['dob'] = moment(user.dob).format('YYYY-MM-DD');
        }
        else if (this.currentStep === 4) {
            cibilRequestData['dob'] = moment(this.tellUsAboutYourselfForm.value.dob).format('YYYY-MM-DD');
            cibilRequestData['postal'] = this.tellUsAboutYourselfForm.value.pincode;
        }
        if (this.previousCibilResponse) {
            if (this.previousCibilResponse.responseStatus.statusCode === ResponseStatusCode.EMAIL_VERIFICATION_CODE_REQUIRED) {
                if ((!this.emailOtpResendAttemptUsed)) {
                    cibilRequestData['status'] = UserInputTypes.USER_INPUT_REQUIRED;
                    cibilRequestData["userInputs"] = {
                        ...this.previousCibilResponse.userInputs,
                        "USER_INPUT_TYPE": UserInputTypes.VERIFICATION_CODE,
                        "AUTH_ANSWER": authAnswer
                    }
                } else {
                    cibilRequestData['status'] = UserInputTypes.REGENERATE_VERIFICATION_CODE;
                    this.emailOtpResendAttemptUsed = false;
                }

            } else if (this.previousCibilResponse.responseStatus.statusCode === ResponseStatusCode.ALTERNATE_MOBILE_NO_REQUIRED) {
                cibilRequestData['status'] = UserInputTypes.USER_INPUT_REQUIRED;
                cibilRequestData["userInputs"] = {
                    ...this.previousCibilResponse.userInputs,
                    "USER_INPUT_TYPE": UserInputTypes.ALTERNATIVE_NUMBER,
                    "AUTH_ANSWER": authAnswer
                }
            } else if (this.previousCibilResponse.responseStatus.statusCode === ResponseStatusCode.MOBILE_VERIFICATION_CODE_REQUIRED) {
                if (!this.alternativeMobileOtpResendAttemptUsed) {
                    cibilRequestData['status'] = UserInputTypes.USER_INPUT_REQUIRED;
                    cibilRequestData["userInputs"] = {
                        ...this.previousCibilResponse.userInputs,
                        "USER_INPUT_TYPE": UserInputTypes.VERIFICATION_CODE,
                        "AUTH_ANSWER": authAnswer
                    }
                } else {
                    cibilRequestData['status'] = UserInputTypes.REGENERATE_VERIFICATION_CODE;
                    this.alternativeMobileOtpResendAttemptUsed = false;
                }

            }
        }

        try {
            this.isLoading = true;
            return await this.cibilService.getCibilReport(cibilRequestData);
        } catch (err) {
            Logger.log(`Unable to get credit score for ${user._id} ${err}`);
            Toaster.toast(`Unable to get you credit score right now. Please try again later`, Type.Error);
            if (this.previousCibilResponse &&
                (this.previousCibilResponse.responseStatus.statusCode === ResponseStatusCode.ALTERNATE_MOBILE_NO_REQUIRED ||
                    this.previousCibilResponse.responseStatus.statusCode === ResponseStatusCode.MOBILE_VERIFICATION_CODE_REQUIRED)) {
                this.matDialog.closeAll();
            }
        } finally {
            this.isLoading = false;
        }
    }

    private async redirectToCibilDashboard(data: CibilRedirectionData) {
        await this.loginHelper.redirectCibilDashboard(data);
    }
}